import { useState, useEffect } from 'react'
import {
  IconButton,
  Box,
  useColorModeValue,
  Tooltip,
  keyframes
} from '@chakra-ui/react'
import { FiArrowUp } from 'react-icons/fi'
import { PRIMARY_COLOR } from '../config/theme'

// Animation for the button appearance
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`

const slideDown = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(20px); }
`

// Configurable scroll threshold
const SCROLL_THRESHOLD = 300

export default function BackToTop() {
  const [isVisible, setIsVisible] = useState(false)
  const [shouldRender, setShouldRender] = useState(false)
  
  const bgColor = useColorModeValue(PRIMARY_COLOR, 'gray.700')
  const hoverBgColor = useColorModeValue('red.600', 'gray.600')
  const shadowColor = useColorModeValue(
    '0 4px 14px rgba(0, 0, 0, 0.25)',
    '0 4px 14px rgba(0, 0, 0, 0.5)'
  )

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > SCROLL_THRESHOLD
      
      if (scrolled && !isVisible) {
        setShouldRender(true)
        // Small delay to allow render before animation
        setTimeout(() => setIsVisible(true), 10)
      } else if (!scrolled && isVisible) {
        setIsVisible(false)
        // Wait for exit animation before removing from DOM
        setTimeout(() => setShouldRender(false), 300)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isVisible])

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  if (!shouldRender) return null

  return (
    <Box
      position="fixed"
      bottom={{ base: 4, md: 8 }}
      right={{ base: 4, md: 8 }}
      zIndex={999}
      animation={`${isVisible ? slideUp : slideDown} 0.3s ease-out forwards`}
    >
      <Tooltip label="Back to top" placement="left" hasArrow>
        <IconButton
          icon={<FiArrowUp size={20} />}
          onClick={scrollToTop}
          aria-label="Scroll to top"
          bg={bgColor}
          color="white"
          size="lg"
          isRound
          boxShadow={shadowColor}
          _hover={{
            bg: hoverBgColor,
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)'
          }}
          _active={{
            transform: 'translateY(0)',
            boxShadow: shadowColor
          }}
          transition="all 0.2s ease"
        />
      </Tooltip>
    </Box>
  )
}
